import { Button } from '@/components/ui/button';
import { REPLAY_TEXT_FIELDS, replayCountSentence, touchedSentence } from '@/lib/replay-settings';
import { REPLAY_SETTINGS_NOTE_MAX, type ReplayTextKey } from '@matter_extractor/shared';
import { useId } from 'react';

/**
 * « Modifications non enregistrées » (docs/specs/rejeu.md §10.3): the box that
 * appears the moment a text moves, under the three fields.
 *
 * It is the box of the seats of Réglages > Modèles - the same bordered frame,
 * the same sentence that announces the line of the journal BEFORE it is written,
 * « Pourquoi (facultatif) », « Enregistrer et journaliser », and the way back -
 * with the three differences the instruction asks for:
 *
 * - it names the TEXTS that moved, not a path;
 * - it says the replays already begun keep the instruction they received (§10.1:
 *   each replay freezes its own copy), which is what makes saving here safe;
 * - there is no « Qui » field. The author of a save is the operator of the
 *   session, read by the server; a free name would let anybody sign as somebody
 *   else in a journal whose whole worth is that it is true.
 *
 * `replayCount` is announced before the move and not discovered in the journal
 * afterwards: it is the figure that carries the honesty of the line - a text
 * rewritten at zero replays is a calibration, the same rewrite at forty is a
 * change of instrument.
 */

export interface ReplayUnsavedBoxProps {
  /** The texts that differ from the ones in force: never empty when the box is drawn. */
  readonly touched: readonly ReplayTextKey[];
  /** How many replays were already played: what the next line will be stamped with. */
  readonly replayCount: number;
  readonly note: string;
  readonly saving: boolean;
  /** The save is closed: a text would be refused. */
  readonly blocked: boolean;
  /** The sentence of the last failed save, or null. */
  readonly failure: string | null;
  readonly onNote: (note: string) => void;
  readonly onSave: () => void;
  readonly onRevert: () => void;
}

export function ReplayUnsavedBox({
  touched,
  replayCount,
  note,
  saving,
  blocked,
  failure,
  onNote,
  onSave,
  onRevert,
}: ReplayUnsavedBoxProps) {
  const noteId = useId();
  // The order of the fields is the order of the screen, whatever the caller sends.
  const ordered = REPLAY_TEXT_FIELDS.map((field) => field.key).filter((key) =>
    touched.includes(key),
  );

  return (
    <form
      data-testid="rejeu-modifications"
      onSubmit={(event) => {
        event.preventDefault();
        if (!blocked && !saving) onSave();
      }}
      className="flex flex-col gap-3 rounded-lg border-2 border-bleu-nuit p-4"
    >
      <p className="text-gray-800 text-sm leading-5">
        <strong className="text-jaune-deep">Modifications non enregistrées</strong> :{' '}
        <span data-testid="rejeu-modifications-textes">{touchedSentence(ordered)}</span>. Cet
        enregistrement sera inscrit au journal, daté, avec{' '}
        <strong data-testid="rejeu-modifications-compte">{replayCountSentence(replayCount)}</strong>{' '}
        à ce moment-là. Les rejeux commencés gardent la consigne qu’ils ont reçue.
      </p>
      <div className="flex max-w-2xl flex-col gap-1">
        <label htmlFor={noteId} className="font-semibold text-gray-700 text-sm">
          Pourquoi (facultatif)
        </label>
        <input
          id={noteId}
          type="text"
          data-testid="rejeu-pourquoi"
          value={note}
          maxLength={REPLAY_SETTINGS_NOTE_MAX}
          disabled={saving}
          onChange={(event) => onNote(event.currentTarget.value)}
          className="h-11 w-full rounded-md border-2 border-gray-200 px-3 text-gray-800 text-sm focus-visible:border-bleu-nuit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
        />
      </div>
      {failure === null ? null : (
        <p role="alert" data-testid="rejeu-echec" className="text-rouge-deep text-sm">
          {failure}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" data-testid="rejeu-enregistrer" disabled={saving || blocked}>
          {saving ? 'Enregistrement…' : 'Enregistrer et journaliser'}
        </Button>
        <Button
          type="button"
          variant="outline"
          data-testid="rejeu-revenir"
          onClick={onRevert}
          disabled={saving}
        >
          Revenir aux textes en vigueur
        </Button>
      </div>
    </form>
  );
}
